import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { AccessTokenType, PermissionKeys, ProfileService, TokenApiService, UserRole } from 'library-explorer';
import { Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from './api/user.service';
import { BrowserNavigationService, DeferredNavigation } from './browser-navigation/browser-navigation.service';
import { BasicPage } from '@app/model/basic-page.model';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class AdminLinkHelperService {
  private currentPageAdminPath: string = '';

  private requiredPermissions: PermissionKeys[] = [];

  private currentUserPermissions: PermissionKeys[] = null;

  constructor(
    private readonly browserNavigationService: BrowserNavigationService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly tokenApiService: TokenApiService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly profileService: ProfileService,
    private readonly settingsService: SettingsService) { }

  public initializeCurrentPageAdminPath(url: string = '', permissionKeys: PermissionKeys[] = []): void {
    this.currentPageAdminPath = url;
    this.requiredPermissions = permissionKeys;
  }

  public initializeAdminPathForBasicPage(page: BasicPage): void {
    const pagePath = page.hasSlides ? `pages/builder/${page.uuid}` : `pages/${page.uuid}`;
    this.initializeCurrentPageAdminPath(pagePath);
  }

  public navigateToAdminPlatform(path?: string): void {
    const deferredNavigation = this.browserNavigationService.beginDeferredNavigation();
    const user = this.profileService.getCurrentProfileValue();
    const adminPlatformUrl = environment.adminBaseDomain;
    const routerSnapshot = this.getLastActivatedRoute(this.activatedRoute.snapshot);
    const routeData = routerSnapshot.data;
    const url = path || (routeData.adminPlatformUrl ? routeData.adminPlatformUrl(routerSnapshot.params) : this.currentPageAdminPath);
    const permissions = routeData.permission || this.requiredPermissions;

    this.settingsService.getSettings()
      .pipe(
        mergeMap(settings => {
          const branchId = settings?.branch?.currentBranch?.branchId;

          return this.checkForPermissions(permissions)
            .pipe(
              mergeMap(hasPermission => {
                if (!hasPermission) {
                  this.router.navigate(['/403'], { queryParams: { message: 'COMMON.pleases_check_with_your_administrator' } });
                  return of(null);
                }

                return this.tokenApiService.getTokenForFileAccess(AccessTokenType.LOGIN, user.id);
              }),
              map(token => ({ token, branchId }))
            );
        })
      )
      .subscribe({
        next: ({ token, branchId }) => {
          if (!token) {
            deferredNavigation.cancel();
            return;
          }

          const fallbackUrl = 'dashboard';
          const adminUrl = `${adminPlatformUrl}/access/login?noAccessRedirect=/${fallbackUrl}&logoutOnUserMismatch=true&target_url=/${url}&status=1&jwt=${token}${branchId ? `&branchId=${branchId}` : ''}`;

          deferredNavigation.navigate(adminUrl);
        },
        error: () => deferredNavigation.cancel()
      });
  }

  private checkForPermissions(requiredPermissions: PermissionKeys[]): Observable<boolean> {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return of(true);
    }

    return this.getCurrentUserPermissions()
      .pipe(
        map(userPermissions => {
          return !requiredPermissions.some(requiredPermission => userPermissions.indexOf(requiredPermission) === -1);
        })
      )
  }

  private getCurrentUserPermissions(): Observable<PermissionKeys[]> {
    if (this.currentUserPermissions) {
      return of(this.currentUserPermissions);
    }

    const userId = this.profileService.getCurrentProfileValue()?.id;

    return this.userService.getUserPermissions(userId)
      .pipe(
        tap(permissions => this.currentUserPermissions = permissions)
      );
  }

  private getLastActivatedRoute(shapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    if (shapshot.firstChild) {
      return this.getLastActivatedRoute(shapshot.firstChild);
    }

    return shapshot;
  }
}
